import * as Sentry from '@sentry/react';
import type { Middleware } from '../runtime';
import { ApiError } from './error-toast-middleware';

export type TokenGetter = (options?: {
  forceRefresh?: boolean;
}) => Promise<string>;

type CachedToken = {
  token: string;
  expiresAt: number;
};

const parseJwtExpiration = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    );
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
};

// Buffer time before expiration to refresh token (5 minutes)
export const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const setAuthorizationHeader = (
  init: RequestInit,
  token: string,
): RequestInit => {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
};

export const createAuthMiddleware = (getAccessToken: TokenGetter): Middleware => {
  let cachedToken: CachedToken | null = null;
  let tokenRefreshPromise: Promise<string> | null = null;

  const getToken = async (forceRefresh = false): Promise<string> => {
    const now = Date.now();

    // Return cached token if it's still valid (with buffer time)
    if (
      !forceRefresh &&
      cachedToken &&
      cachedToken.expiresAt - TOKEN_EXPIRY_BUFFER_MS > now
    ) {
      return cachedToken.token;
    }

    // If a refresh is already in progress, wait for it
    if (tokenRefreshPromise) {
      return tokenRefreshPromise;
    }

    // Start a new token refresh
    tokenRefreshPromise = (async () => {
      try {
        const token = await getAccessToken({ forceRefresh });

        // If we can't parse expiration, cache for 1 hour
        const expiresAt = parseJwtExpiration(token) ?? now + 60 * 60 * 1000;
        cachedToken = { token, expiresAt };

        return token;
      } catch (error) {
        cachedToken = null;
        Sentry.captureException(error, { tags: { area: 'auth-token-refresh' } });
        // Surface as 401 so the UI shows the "Session expired" notification
        throw new ApiError('Unable to refresh the session token', 401);
      } finally {
        tokenRefreshPromise = null;
      }
    })();

    return tokenRefreshPromise;
  };

  return {
    pre: async (context) => {
      const token = await getToken();

      return {
        url: context.url,
        init: setAuthorizationHeader(context.init, token),
      };
    },

    post: async ({ url, init, response }) => {
      if (response.status !== 401) return response;

      // Refresh the token once and retry the request. The retry calls
      // `fetch` directly, so it does not pass through this middleware again.
      cachedToken = null;
      const token = await getToken(true);
      const retryResponse = await fetch(url, setAuthorizationHeader(init, token));

      if (retryResponse.status === 401) {
        Sentry.captureMessage('API returned 401 after token refresh', {
          level: 'warning',
          tags: { area: 'auth-repeated-401' },
          extra: { url },
        });
      }

      return retryResponse;
    },
  };
};
